import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import { apiFetch, ApiRequestError } from "../../api/client";
import type { components } from "../../api/generated";
import { RecommendationError, RecommendationLoading, RecommendationView } from "../recommendation";
import { Turnstile } from "./Turnstile";
import styles from "./Planner.module.css";

type RecommendationResponse = components["schemas"]["RecommendationResponseV1"];
type UIConfig = components["schemas"]["UIConfigV1"];

const teamInput = z.string().trim().refine((value) => /^\d+$/.test(value) && Number(value) > 0 || /^(?:https?:\/\/)?(?:www\.)?fantasy\.premierleague\.com\/entry\/\d+(?:\/event\/\d+)?\/?$/i.test(value), "Enter a valid FPL team ID or public team URL.");

function getTeamId(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/\/entry\/(\d+)(?:\/|$)/i);
  return match?.[1] ?? trimmed;
}

function errorMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return "We couldn’t build that recommendation. Please try again.";
  if (error.status === 403) return "Verification failed; please try again.";
  if (error.status === 422) return "We couldn’t load that FPL team. Check the ID or URL and try again.";
  if (error.status === 502) return "FPL is currently unavailable. Please try again in a few minutes.";
  if (error.status === 503) return "A forecast is not available yet. Please try again after the next refresh.";
  return "We couldn’t build that recommendation. Please try again.";
}

export function Planner() {
  const [teamValue, setTeamValue] = useState("");
  const [validationError, setValidationError] = useState<string>();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const resultRef = useRef<HTMLDivElement>(null);
  const config = useQuery({ queryKey: ["ui-config"], queryFn: () => apiFetch<UIConfig>("/api/ui-config"), staleTime: Infinity });
  const mutation = useMutation({
    mutationFn: (teamId: string) => apiFetch<RecommendationResponse>("/api/recommendations", { method: "POST", body: { team_id: teamId, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}) } }),
    onError: () => { setTurnstileToken(null); setTurnstileResetKey((key) => key + 1); },
  });

  const siteKey = config.data?.turnstile_site_key ?? null;
  const submitTeam = () => {
    const parsed = teamInput.safeParse(teamValue);
    if (!parsed.success) { setValidationError(parsed.error.issues[0]?.message); return; }
    if (config.isPending) return;
    if (siteKey && !turnstileToken) { setValidationError("Complete the verification first."); return; }
    setValidationError(undefined);
    mutation.mutate(getTeamId(parsed.data));
  };

  useEffect(() => {
    if (!mutation.data?.recommendation || !resultRef.current) return;
    const heading = resultRef.current.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, [mutation.data]);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitTeam();
  };

  return <div className={styles.planner}>
    <div className={styles.hero}>
      <section className={styles.intro} aria-labelledby="planner-title">
        <p className="eyebrow">Your next gameweek starts here</p>
        <h1 id="planner-title">Know your next move.</h1>
        <p className="lede">Get a clear transfer decision, captain and lineup for your FPL team. See the expected impact and compare other ways to play it.</p>
        <p className={styles.publicNote}>One gameweek · Public team data · No sign-in needed</p>
      </section>
      <form className={styles.form} onSubmit={submit} noValidate>
        <div><p className={styles.formEyebrow}>Find your team</p><h2>Build your gameweek plan</h2><p>Enter your public FPL team ID or paste its URL.</p></div>
        <Field label="FPL team ID or public team URL" name="teamId" value={teamValue} onChange={(event) => { setTeamValue(event.target.value); if (validationError) setValidationError(undefined); }} placeholder="123456 or fantasy.premierleague.com/entry/123456" autoComplete="off" error={validationError} />
        {siteKey && <Turnstile siteKey={siteKey} onToken={setTurnstileToken} resetKey={turnstileResetKey} />}
        <div className={styles.actions}><Button type="submit" disabled={config.isPending || mutation.isPending}>{mutation.isPending ? "Building plan…" : "Show my plan"}</Button></div>
        <p className={styles.signin}>Want saved plans and a longer view? <Link to="/auth/login">Sign in</Link></p>
        {mutation.isPending && <p className={styles.progress} role="status" aria-live="polite">Building your recommendation…</p>}
      </form>
    </div>
    {config.isError && <Alert tone="info">Security verification is temporarily unavailable. You can still try your team ID.</Alert>}
    {mutation.isError && <RecommendationError message={errorMessage(mutation.error)} onRetry={submitTeam} />}
    {mutation.isPending && <RecommendationLoading />}
    {mutation.data?.recommendation && <div className={styles.result} ref={resultRef}><RecommendationView recommendation={mutation.data.recommendation} /></div>}
  </div>;
}
